import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { routes } from './app.routes';
import { catchError, EMPTY, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { AuthService } from './core/services/auth-service/auth.service';
import { GlobalErrorHandler } from './core/error-handlers/global-error-handler.class';
import { UserService } from './core/services/user-service/user.service';
import { HttpConfigInterceptor } from './core/interceptors/http-config-interceptor/http-config.interceptor';
import { UserConfigurationService } from './core/services/userconfiguration/user-configuration.service';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  FALLBACK_LANGUAGE,
  getBrowserPreferredLanguages,
  resolvePreferredLanguage,
  SUPPORTED_LANGUAGES,
} from './core/helpers/language.helper';

function initializer(): () => Observable<void> {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const translate = inject(TranslateService);
  const userConfiguration = inject(UserConfigurationService);

  translate.addLangs([...SUPPORTED_LANGUAGES]);

  return () =>
    forkJoin([
      loadPreferredLanguage(translate),
      authService.checkAuthentication().pipe(
        take(1),
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            // We got 401 while trying to get user data.
            // This means we are not logged in.
            // Try to authorize user by entra id.
            authService.redirectToAzureAd();
            return EMPTY;
          } else {
            // For all other auth errors - redirect to the error page.
            authService.redirectToAuthErrorPage();
            return EMPTY;
          }
        }),
        tap((_user) => {
          userService.setUser(_user);
        }),
        switchMap(() => userConfiguration.fetchUserConfiguration()),
      ),
    ]).pipe(map(() => undefined));
}

/**
 * Switches to the browser's preferred language and completes only once its translation file has
 * arrived. The app has to wait for it: ngx-translate keeps serving the English fallback until the
 * file is loaded, and anything that resolves a translation once during startup - translate.instant()
 * inside a computed(), for instance - would otherwise keep that English text for good.
 *
 * A failed load must not block the start, so it falls back to English instead of erroring.
 */
function loadPreferredLanguage(translate: TranslateService): Observable<void> {
  return translate.use(resolvePreferredLanguage(getBrowserPreferredLanguages())).pipe(
    take(1),
    map(() => undefined),
    catchError(() => of(undefined)),
  );
}

const initializerProvider = {
  provide: APP_INITIALIZER,
  useFactory: initializer,
  multi: true,
};

const globalErrorHandler = {
  provide: ErrorHandler,
  useClass: GlobalErrorHandler,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withEnabledBlockingInitialNavigation(), withComponentInputBinding()),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json',
      }),
      fallbackLang: FALLBACK_LANGUAGE,
      lang: FALLBACK_LANGUAGE,
    }),
    initializerProvider,
    globalErrorHandler,
  ],
};
